package dev.lingvo.site.pages.app.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.MemberDto
import dev.lingvo.shared.Role
import dev.lingvo.site.ds.LAvatar
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBulkActionBar
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LMenu
import dev.lingvo.site.ds.LMenuEntry
import dev.lingvo.site.ds.LMenuItem
import dev.lingvo.site.ds.LMenuPlacement
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSearchField
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LSwitch
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTableSelectAllHeaderCell
import dev.lingvo.site.ds.LTableSelection
import dev.lingvo.site.ds.LTableSelectionCellStyle
import dev.lingvo.site.ds.LToggleChip
import dev.lingvo.site.ds.LTrashIcon
import dev.lingvo.site.ds.LUsersIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.nav.label
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/** The id the select-all header cell carries, so clearing a selection can hand focus back to it. */
internal const val MEMBERS_SELECT_ALL_ID = "members-select-all"

/**
 * Who matched the search and the role chips, and which of those rows can be acted on together.
 *
 * A pure function so the interesting part — "select all" means *these* rows, not every row the
 * workspace has — is testable without a browser. A row nobody may remove is never selectable, which
 * is the whole reason `selectable` is narrower than `visible`: offering a checkbox on the last owner
 * and then refusing the action is exactly the dishonesty the authority rule forbids.
 */
internal fun rosterView(
    members: List<MemberDto>,
    query: String,
    roles: Set<Role>,
): RosterView {
    val needle = query.trim().lowercase()
    val visible = members.filter { member ->
        (roles.isEmpty() || member.role in roles) &&
            (
                needle.isEmpty() ||
                    member.name.lowercase().contains(needle) ||
                    member.email.lowercase().contains(needle)
                )
    }
    return RosterView(
        visible = visible,
        selectableIds = visible.filter { it.capabilities.canRemove }.map { it.userId }.toSet(),
    )
}

internal data class RosterView(
    val visible: List<MemberDto>,
    val selectableIds: Set<String>,
)

/**
 * The state of the select-all box for one selection over one page of rows.
 *
 * `SOME` is deliberately "not yet everything", so one press from a partial selection completes it
 * rather than discarding the work somebody has already done.
 */
internal fun rosterSelectionState(selected: Set<String>, selectable: Set<String>): LTableSelection =
    when {
        selectable.isEmpty() || selected.none { it in selectable } -> LTableSelection.NONE
        selectable.all { it in selected } -> LTableSelection.ALL
        else -> LTableSelection.SOME
    }

/**
 * Everyone in the workspace, filtered, selectable, and acted on one row or many at a time.
 *
 * The filter exists because the table is the whole page on a workspace with forty people and there
 * was no way to answer "who are the admins" other than reading. The selection exists because
 * removing six people who left the same contractor was six confirmations, and the sixth is the one
 * that gets clicked without reading.
 */
@Composable
internal fun MembersRoster(
    members: List<MemberDto>,
    query: String,
    onQuery: (String) -> Unit,
    roleFilter: Set<Role>,
    onRoleFilter: (Set<Role>) -> Unit,
    selected: Set<String>,
    onSelected: (Set<String>) -> Unit,
    onRemove: (MemberDto) -> Unit,
    onRoleChange: (MemberDto, Role) -> Unit,
    onToggleApprove: (MemberDto, Boolean) -> Unit,
    onBulkRemove: () -> Unit,
    onBulkRole: (Role) -> Unit,
    busy: Boolean,
) {
    val view = rosterView(members, query, roleFilter)
    val selectionState = rosterSelectionState(selected, view.selectableIds)
    val chosen = members.filter { it.userId in selected }
    // Only the roles this caller could actually move somebody to, intersected across the selection.
    // A bulk role the server would refuse for one of the six is a bulk action that half-applies.
    val bulkRoles = if (chosen.isEmpty()) {
        emptyList()
    } else {
        Role.entries.filter { role -> chosen.all { role in it.capabilities.assignableRoles } }
    }

    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        Row(
            Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            LSearchField(
                query,
                onQuery,
                label = t("app.settings.members_roster.search_people", "Search people"),
                placeholder = t("app.settings.members_roster.name_or_email", "Name or email"),
                shortcutHint = null,
            )
            Role.entries.forEach { role ->
                val count = members.count { it.role == role }
                LToggleChip(
                    role.label(),
                    selected = role in roleFilter,
                    onSelectedChange = { on ->
                        onRoleFilter(if (on) roleFilter + role else roleFilter - role)
                    },
                    count = count,
                    // A role nobody here holds is still shown, so the chip row is a stable map of
                    // the four roles rather than a list that changes shape as people join.
                    enabled = count > 0,
                )
            }
        }

        if (view.visible.isEmpty()) {
            LEmptyState(
                t("app.settings.members_roster.nobody_matches_that", "Nobody matches that"),
                t(
                    "app.settings.members_roster.widen_the_search_or_clear_the",
                    "Widen the search, or clear the role chips to see everyone again.",
                ),
                icon = LEmptyStateIcon.SEARCH,
            ) {
                LButton(
                    t("app.settings.members_roster.show_everyone", "Show everyone"),
                    { onQuery(""); onRoleFilter(emptySet()) },
                    variant = LButtonVariant.SECONDARY,
                    leadingIcon = { LUsersIcon() },
                )
            }
            return@Column
        }

        LTable(caption = t("app.settings.members_roster.members", "Members")) {
            LTableHead {
                LTableRow {
                    LTableSelectAllHeaderCell(
                        state = selectionState,
                        onChange = { everything ->
                            onSelected(if (everything) view.selectableIds else emptySet())
                        },
                        label = t(
                            "app.settings.members_roster.select_every_member_you_can_remove",
                            "Select every member you can remove",
                        ),
                        modifier = Modifier.attr("id", MEMBERS_SELECT_ALL_ID),
                    )
                    LTableHeaderCell { Text(t("app.settings.members_roster.name", "Name")) }
                    LTableHeaderCell { Text(t("app.settings.members_roster.email", "Email")) }
                    LTableHeaderCell { Text(t("app.settings.members_roster.role", "Role")) }
                    LTableHeaderCell { Text(t("app.settings.members_roster.can_approve", "Can approve")) }
                    LTableHeaderCell { Text(t("app.settings.members_roster.action", "Action")) }
                }
            }
            LTableBody {
                view.visible.forEach { member ->
                    val can = member.capabilities
                    val selectable = member.userId in view.selectableIds
                    LTableRow(
                        selected = member.userId in selected,
                        selectable = selectable,
                        onSelectedChange = { on ->
                            onSelected(
                                if (on) selected + member.userId else selected - member.userId,
                            )
                        },
                        selectionLabel = trf(
                            "app.settings.members_roster.select_email",
                            "Select {email}",
                            "email" to member.email,
                        ),
                    ) {
                        // **The column has to exist even when the checkbox does not.**
                        // `LTableRow` emits its selection cell only when the row is selectable, and
                        // a row is selectable only when this reader may remove that member — which
                        // the last owner never is. On a one-owner workspace that meant the header
                        // had six cells and the single row had five: the name sat under the
                        // select-all checkbox, the email under NAME, and every fact on the page was
                        // reported one column left of the word describing it. An empty cell in the
                        // same narrow column keeps the row on its headers.
                        if (!selectable) LTableCell(LTableSelectionCellStyle.toModifier()) {}
                        LTableHeaderCell(scope = LTableHeaderScope.ROW) {
                            Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                                LAvatar(member.name)
                                Text(
                                    if (member.isSelf) {
                                        tf(
                                            "app.settings.members_roster.name_you",
                                            "{name} (you)",
                                            "name" to member.name,
                                        )
                                    } else {
                                        member.name
                                    },
                                )
                            }
                        }
                        LTableCell { Text(member.email) }
                        LTableCell {
                            if (can.canChangeRole) {
                                LSelect(
                                    member.role.name,
                                    can.assignableRoles.map { LSelectOption(it.name, it.label()) },
                                    { value ->
                                        val next = Role.entries.firstOrNull { it.name == value }
                                        if (next != null && next != member.role) {
                                            onRoleChange(member, next)
                                        }
                                    },
                                    label = null,
                                    accessibleLabel = trf(
                                        "app.settings.members_roster.role_for_email",
                                        "Role for {email}",
                                        "email" to member.email,
                                    ),
                                )
                            } else {
                                LBadge(member.role.label())
                            }
                            can.blockedReason?.let { reason ->
                                P(
                                    Modifier
                                        .color(ColorMode.current.toSitePalette().textSecondary)
                                        .fontSize(TypeScale.Small)
                                        .margin(top = Space.XS, bottom = 0.px)
                                        .toAttrs(),
                                ) {
                                    Text(reason)
                                }
                            }
                        }
                        LTableCell {
                            when {
                                can.canToggleApprove -> LSwitch(
                                    member.canApprove,
                                    { next -> onToggleApprove(member, next) },
                                    label = t("app.settings.members_roster.reviewer_free_no_billable_seat", "Reviewer — free, no billable seat"),
                                )
                                member.canApprove -> LBadge(t("app.settings.members_roster.yes", "Yes"))
                                else -> Text("—")
                            }
                        }
                        LTableCell {
                            if (can.canRemove) {
                                LButton(
                                    t("app.settings.members_roster.remove", "Remove"),
                                    { onRemove(member) },
                                    variant = LButtonVariant.GHOST,
                                    size = LButtonSize.SM,
                                    leadingIcon = { LTrashIcon() },
                                    // Eight rows of "Remove" are eight identical announcements; the
                                    // row header is not read again when the button itself is focused.
                                    accessibleLabel = tf(
                                        "app.settings.members_roster.remove_email",
                                        "Remove {email}",
                                        "email" to member.email,
                                    ),
                                )
                            } else if (member.isSelf) {
                                // The one row whose missing button has no explanation beside it:
                                // `blockedReason` is null for your own row, so an empty cell was
                                // the whole of the answer.
                                LMuted(t("app.settings.members_roster.you_cannot_remove_yourself", "You cannot remove yourself"))
                            } else {
                                // An em dash, like the approval column, so the cell reads as
                                // deliberately empty rather than as a control that failed to draw.
                                // The reason is on the role cell beside it.
                                Text("—")
                            }
                        }
                    }
                }
            }
        }
    }

    val roleEntries: List<LMenuEntry> = bulkRoles.map { role ->
        LMenuItem(
            trf(
                "app.settings.members_roster.make_them_role",
                "Make them {role}",
                "role" to role.label(),
            ),
            icon = { LUsersIcon() },
        )
    }
    // Two honesty lines, whichever apply, joined into the bar's one note.
    //
    // The first accounts for a control that is not there. `bulkRoles` is the intersection of what
    // every selected person may be moved to, and on a mixed selection that intersection is
    // routinely empty — so "Change role" simply vanishes and the reader is left to guess whether
    // they misread the bar or the product is broken. Refusing honestly means saying what is
    // refusing, and a control that has silently gone missing says nothing at all.
    //
    // The second: a selection made under a filter covers what the filter showed, and somebody who
    // then clears the filter must not believe the other thirty rows came with it.
    val mixedSelectionNote = if (roleEntries.isEmpty() && selected.size > 1) {
        tf(
            "app.settings.members_roster.no_one_role_fits_all_count_of",
            "No one role fits all {count} of these people, so change roles one at a time.",
            "count" to selected.size.toString(),
        )
    } else {
        null
    }
    val filteredSelectionNote = if (query.isNotBlank() || roleFilter.isNotEmpty()) {
        tf(
            "app.settings.members_roster.selection_covers_the_count_rows_this",
            "Selection covers the {count} rows this filter shows.",
            "count" to view.visible.size.toString(),
        )
    } else {
        null
    }
    val bulkNote = listOfNotNull(mixedSelectionNote, filteredSelectionNote)
        .joinToString(" ")
        .takeIf { it.isNotEmpty() }

    LBulkActionBar(
        selectedCount = selected.size,
        onClear = { onSelected(emptySet()) },
        label = tPlural(
            "app.settings.members_roster.count.person_selected",
            selected.size,
            "{count} person selected",
            "{count} people selected",
        ),
        note = bulkNote,
        returnFocusToId = MEMBERS_SELECT_ALL_ID,
    ) {
        // Only offered when every selected row can actually take every offered role. A menu that
        // half-applies is worse than no menu: the receipt has to explain a state nobody asked for.
        if (roleEntries.isNotEmpty()) {
            var menuOpen by remember { mutableStateOf(false) }
            LMenu(
                expanded = menuOpen,
                entries = roleEntries,
                onExpandedChange = { menuOpen = it },
                onSelect = { index ->
                    menuOpen = false
                    bulkRoles.getOrNull(index)?.let(onBulkRole)
                },
                placement = LMenuPlacement.TOP_START,
            ) { onToggle, triggerModifier ->
                LButton(
                    t("app.settings.members_roster.change_role", "Change role"),
                    onToggle,
                    modifier = triggerModifier,
                    size = LButtonSize.SM,
                    variant = LButtonVariant.SECONDARY,
                    enabled = !busy,
                    leadingIcon = { LUsersIcon() },
                )
            }
        }
        LButton(
            tPlural(
                "app.settings.members_roster.count.remove_person",
                selected.size,
                "Remove {count} person",
                "Remove {count} people",
            ),
            onBulkRemove,
            size = LButtonSize.SM,
            variant = LButtonVariant.DANGER,
            enabled = !busy,
            leadingIcon = { LTrashIcon() },
        )
    }
}
